<template>
  <family-page-wrapper class="pb-20">
    <template v-if="isRidesExist">
      <h5>Here's the list of the rides on your account.</h5>

      <pagination
        :total-pages="paginationInfo.totalPages"
        :current-page="paginationInfo.currentPage"
        @load-page-data="getRides"
      />

      <ride-history-card
        :rides="rides"
        @show-gift-dialog="showGiftDialog"
      />
    </template>

    <template v-else>
      <div
        v-if="!status.resultHtml"
        class="form-submit-button-group"
      >
        <p-button
          type="purple"
          round
          @click="scheduleRide"
        >
          Schedule a Ride
        </p-button>

        <p-button
          type="primary"
          round
          @click="orderRide"
        >
          Order a Ride
        </p-button>
      </div>

      <message-html
        v-else
        class="mt-20"
        :result-html="status.resultHtml"
      />
    </template>

    <give-gift-tip-dialog
      :is-show-dialog="status.isShowGiftDialog"
      @submit="giveGiftTip"
      @cancel="status.isShowGiftDialog = false"
    />
  </family-page-wrapper>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { sendGet } from '@/Utils/api';
import rideUtility from '@/Utils/rideUtility';
import { rideService } from '@/services/ride';

const RideHistoryCard = defineAsyncComponent(() => import('@/components/UIComponents/RideHistoryCardView.vue') as any);

const GiveGiftTipDialog = defineAsyncComponent(() => import('../Dialogs/GiveGiftTip.vue') as any);

const paginationLimit = 10;

const router = useRouter();

const { customerInfo } = useCustomer();

const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const rides: Ref<any> = ref([]);
const selectedRideIndex = ref(-1);
const status = reactive({
  resultHtml: '',
  isShowGiftDialog: false,
});
const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});

const isRidesExist = computed(() => rides.value.length !== 0);

const getRides = async (currentPage: number = 1) => {
  if (!customerInfo.value?.id) {
    return;
  }

  status.resultHtml = '';
  paginationInfo.currentPage = currentPage;

  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Ride History...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    const query = {
      callerId: customerInfo.value.id,
      limit: paginationLimit,
      offset: (currentPage - 1) * paginationLimit,
    };

    const response = await sendGet('rides', query);

    paginationInfo.totalPages = Math.ceil(response.data.count / paginationLimit) || 1;

    rides.value = response.data.rows.map((ride: any) => rideUtility.formatRideData(ride));
  } catch (error) {
    rides.value = [];

    paginationInfo.totalPages = 1;
    paginationInfo.currentPage = 1;

    status.resultHtml = util.getErrorResponse(error);
  }
  loading.close();
};

const showGiftDialog = (index: number) => {
  selectedRideIndex.value = index;
  status.isShowGiftDialog = true;
};

const giveGiftTip = async ({ notes, tipAmount }: { notes: string; tipAmount: number }) => {
  status.isShowGiftDialog = false;

  const loading = ElLoading.service({
    lock: true,
    text: 'Tipping driver...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });
  try {
    await rideService.tipDriver({
      notes,
      tipAmount,
      requestId: rides.value[selectedRideIndex.value].requestId,
    });
    showSuccessPopupDialog({
      text: `The driver has been tipped $${tipAmount} on behalf of the customer.`,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  loading.close();
};

const orderRide = () => {
  router.push('/family/ride-ordering?rideFormType=order');
};

const scheduleRide = () => {
  router.push('/family/ride-ordering');
};

getRides();
</script>
